import type { AudioEvent, Instrument } from '../domain/types'

type AudioWindow = Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext }

function midiToFrequency(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12)
}

class TonalAudioEngine {
  private context: AudioContext | null = null
  private activeNodes = new Set<AudioScheduledSourceNode>()

  private getContext(): AudioContext {
    if (!this.context) {
      const AudioConstructor = window.AudioContext ?? (window as AudioWindow).webkitAudioContext
      if (!AudioConstructor) throw new Error('Web Audio is not supported in this browser.')
      this.context = new AudioConstructor()
    }
    return this.context
  }

  async play(events: AudioEvent[], instrument: Instrument): Promise<void> {
    const context = this.getContext()
    if (context.state === 'suspended') await context.resume()
    this.stop()

    const start = context.currentTime + 0.035
    const master = context.createGain()
    const compressor = context.createDynamicsCompressor()
    compressor.threshold.value = -18
    compressor.knee.value = 12
    compressor.ratio.value = 4
    master.gain.value = instrument === 'tonewheel' ? 0.18 : 0.22
    master.connect(compressor)
    compressor.connect(context.destination)

    for (const event of events) {
      for (const midi of event.notes) {
        this.scheduleNote(context, master, midi, start + event.offset, event.duration, instrument)
      }
    }

    const end = Math.max(...events.map((event) => event.offset + event.duration), 0.2)
    window.setTimeout(() => {
      try {
        master.disconnect()
        compressor.disconnect()
      } catch {
        // Nodes may already be disconnected when a new question interrupts playback.
      }
    }, (end + 1.5) * 1000)
  }

  stop(): void {
    for (const node of this.activeNodes) {
      try {
        node.stop()
      } catch {
        // A node that has already ended cannot be stopped twice.
      }
    }
    this.activeNodes.clear()
  }

  private scheduleNote(
    context: AudioContext,
    output: AudioNode,
    midi: number,
    start: number,
    duration: number,
    instrument: Instrument,
  ): void {
    const frequency = midiToFrequency(midi)
    const noteGain = context.createGain()
    const filter = context.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(instrument === 'studio-piano' ? 5200 : 7200, start)
    filter.Q.value = 0.7
    noteGain.connect(filter)
    filter.connect(output)

    const release = instrument === 'studio-piano' ? 1.15 : 0.35
    const peak = instrument === 'classic-synth' ? 0.34 : 0.26
    noteGain.gain.setValueAtTime(0.0001, start)
    noteGain.gain.exponentialRampToValueAtTime(peak, start + 0.014)

    if (instrument === 'studio-piano') {
      noteGain.gain.exponentialRampToValueAtTime(0.09, start + Math.min(0.32, duration * 0.45))
      noteGain.gain.exponentialRampToValueAtTime(0.0001, start + duration + release)
      this.addOscillator(context, noteGain, frequency, 'triangle', 0.72, start, duration + release)
      this.addOscillator(context, noteGain, frequency * 2.001, 'sine', 0.20, start, duration + 0.65)
      this.addOscillator(context, noteGain, frequency * 3.004, 'sine', 0.07, start, duration + 0.35)
      this.addOscillator(context, noteGain, frequency * 4.01, 'sine', 0.025, start, duration + 0.2)
      this.addHammerNoise(context, noteGain, start)
    } else if (instrument === 'tonewheel') {
      noteGain.gain.linearRampToValueAtTime(peak, start + 0.04)
      noteGain.gain.setValueAtTime(peak * 0.85, start + duration)
      noteGain.gain.exponentialRampToValueAtTime(0.0001, start + duration + release)
      this.addOscillator(context, noteGain, frequency, 'sine', 0.75, start, duration + release)
      this.addOscillator(context, noteGain, frequency * 2, 'sine', 0.32, start, duration + release)
      this.addOscillator(context, noteGain, frequency * 3, 'sine', 0.12, start, duration + release)
    } else {
      noteGain.gain.exponentialRampToValueAtTime(peak, start + 0.025)
      noteGain.gain.exponentialRampToValueAtTime(0.0001, start + duration + release)
      this.addOscillator(context, noteGain, frequency, 'triangle', 0.78, start, duration + release)
      this.addOscillator(context, noteGain, frequency * 2, 'sine', 0.15, start, duration + release)
    }
  }

  private addOscillator(
    context: AudioContext,
    output: AudioNode,
    frequency: number,
    type: OscillatorType,
    gainValue: number,
    start: number,
    duration: number,
  ): void {
    const oscillator = context.createOscillator()
    const partialGain = context.createGain()
    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequency, start)
    partialGain.gain.value = gainValue
    oscillator.connect(partialGain)
    partialGain.connect(output)
    oscillator.start(start)
    oscillator.stop(start + duration + 0.08)
    this.activeNodes.add(oscillator)
    oscillator.addEventListener('ended', () => this.activeNodes.delete(oscillator))
  }

  private addHammerNoise(context: AudioContext, output: AudioNode, start: number): void {
    const frames = Math.floor(context.sampleRate * 0.025)
    const buffer = context.createBuffer(1, frames, context.sampleRate)
    const data = buffer.getChannelData(0)
    for (let index = 0; index < frames; index += 1) data[index] = (Math.random() * 2 - 1) * (1 - index / frames)
    const source = context.createBufferSource()
    const gain = context.createGain()
    gain.gain.value = 0.055
    source.buffer = buffer
    source.connect(gain)
    gain.connect(output)
    source.start(start)
    this.activeNodes.add(source)
    source.addEventListener('ended', () => this.activeNodes.delete(source))
  }
}

export const audioEngine = new TonalAudioEngine()
